import { defaultLocale, intlLocale, messages, type Locale, type TranslationKey } from "./messages";

export type TranslationValues = Record<string, string | number>;

/** keys translated by count: "count.pizzas" for "count.pizzas.one" / ".few" / ".other" */
export type PluralKey = {
  [K in TranslationKey]: K extends `${infer Base}.one` ? Base : never;
}[TranslationKey];

/**
 * The language of the UI outside React: set by LocalizationProvider before its children render, so that
 * helpers called during render (verdict reasons, date formats, column definitions) follow the choice.
 * Components should still read `t` from useLocalization() so that they re-render when it changes.
 */
let currentLocale: Locale = defaultLocale;

export const getCurrentLocale = () => currentLocale;
export const setCurrentLocale = (locale: Locale) => {
  currentLocale = locale;
};
/** BCP 47 tag of the current language, for Intl and Date formatting */
export const getIntlLocale = () => intlLocale[currentLocale];

const interpolate = (message: string, values?: TranslationValues) =>
  values
    ? Object.entries(values).reduce(
        (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
        message,
      )
    : message;

const lookup = (locale: Locale, key: string): string | undefined =>
  (messages[locale] as Record<string, string>)[key] ??
  (messages.en as Record<string, string>)[key];

/** Text of `key` in the current language (English when Czech lacks it, the key itself when both do). */
export function translate(key: TranslationKey, values?: TranslationValues, locale = currentLocale): string {
  return interpolate(lookup(locale, key) ?? key, values);
}

/** Text of a plural key for `count` ({count} is filled in): 1 pizza / 2 pizzy / 5 pizz. */
export function translatePlural(
  key: PluralKey,
  count: number,
  values?: TranslationValues,
  locale = currentLocale,
): string {
  const category = new Intl.PluralRules(intlLocale[locale]).select(count);
  const form = category === "one" ? "one" : category === "few" ? "few" : "other";
  const message = lookup(locale, `${key}.${form}`) ?? lookup(locale, `${key}.other`) ?? key;
  return interpolate(message, { count, ...values });
}

/** A translation of a free-form key (e.g. a backend reason code), or undefined when there is none. */
export function translateOptional(key: string, values?: TranslationValues, locale = currentLocale) {
  const message = lookup(locale, key);
  return message === undefined ? undefined : interpolate(message, values);
}
